import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { carouselFeatureList } from '@indutrust/shared-data';
import { SCREEN_BREAKPOINTS_PX } from '@indutrust/shared-model';
import {
  provideTranslocoScopeSharedFeaturesCarousel,
  ResizeListenerDirective,
} from '@indutrust/shared-util';
import { TranslocoDirective } from '@jsverse/transloco';
import { Carousel } from 'primeng/carousel';
import { take, timer } from 'rxjs';
import { CarouselFeatureCardComponent } from './carousel-feature-card/carousel-feature-card.component';

@Component({
  selector: 'idt-features-carousel',
  imports: [
    Carousel,
    CarouselFeatureCardComponent,
    ResizeListenerDirective,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t; prefix: 'sharedFc'">
      @if (displayCarousel()) {
        <section [attr.aria-label]="t('regionLabel')">
          <p-carousel
            idtResizeListener
            [autoplayInterval]="autoplayInterval()"
            [circular]="true"
            [numScroll]="1"
            [numVisible]="1"
            [responsiveOptions]="responsiveOptions"
            [value]="features"
            (resizeEvent)="onResize()">
            <ng-template #item let-feature>
              <idt-carousel-feature-card [feature]="feature" />
            </ng-template>
          </p-carousel>
        </section>
      }
    </ng-container>
  `,
  styles: `
    :host {
      display: flex;
      flex: 1;
      width: 100%;
      min-width: 0;
      overflow: hidden;
      align-items: center;

      ::ng-deep .p-carousel-item {
        display: flex;
        align-items: center;
      }
    }

    /* Force the p-carousel element itself to fill the host width */
    p-carousel {
      width: 100%;
      min-width: 0;
    }

    section {
      display: flex;
      flex: 1;
      width: 100%;
      min-width: 0;
      align-items: center;
    }
  `,
  providers: [provideTranslocoScopeSharedFeaturesCarousel()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Responsive features carousel rendering marketing cards.
 *
 * Wraps PrimeNG's carousel and displays {@link CarouselFeatureCard} slides
 * for items from the feature carousel configuration. Pass
 * `[autoplayInterval]="0"` to disable autoplay (auth shell).
 *
 * @example
 * <idt-features-carousel [autoplayInterval]="0" />
 */
export class FeaturesCarouselComponent {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Milliseconds between autoplay advances. Use `0` to disable autoplay.
   */
  readonly autoplayInterval = input(6000);
  readonly currentLanguageDirection = input<'ltr' | 'rtl'>('ltr');

  readonly displayCarousel = signal(true);

  readonly features = carouselFeatureList;
  /** PrimeNG Carousel — breakpoint strings use max-width semantics from {@link SCREEN_BREAKPOINTS_PX}. */
  readonly responsiveOptions = [
    { breakpoint: SCREEN_BREAKPOINTS_PX.xl, numVisible: 1, numScroll: 1 },
    { breakpoint: SCREEN_BREAKPOINTS_PX.lg, numVisible: 1, numScroll: 1 },
    { breakpoint: SCREEN_BREAKPOINTS_PX.md, numVisible: 1, numScroll: 1 },
  ];

  onResize() {
    this.displayCarousel.set(false);
    timer(0)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.displayCarousel.set(true);
      });
  }
}
